import { useState } from 'react';
import { ArrowLeft, Heart, Activity, Gauge, CheckCircle, AlertCircle, ClipboardList, TrendingUp, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { useRecord6MWT } from '../lib/hooks/useTests';
import { useAuth } from '../lib/hooks/useAuth';

interface SixMinuteWalkTestProps {
  onBack?: () => void;
  patientInfo?: {
    name: string;
    age: number;
    gender: 'male' | 'female';
    height: number; // cm
    weight: number; // kg
  };
  selectedPatient?: any;
}

export function SixMinuteWalkTest({ onBack, patientInfo, selectedPatient }: SixMinuteWalkTestProps) {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const { mutateAsync: record6MWT } = useRecord6MWT();
  
  // Get patient info from props, selectedPatient (nurse mode), or auth user
  const patient = selectedPatient ? {
    name: selectedPatient.name || user?.name || 'Patient',
    age: selectedPatient.age || user?.age || 58,
    gender: (selectedPatient.gender || user?.gender || 'male') as 'male' | 'female',
    height: selectedPatient.height || user?.height || 170,
    weight: selectedPatient.weight || user?.weight || 72
  } : (patientInfo || {
    name: user?.name || 'Patient',
    age: user?.age || 58,
    gender: (user?.gender || 'male') as 'male' | 'female',
    height: user?.height || 170,
    weight: user?.weight || 72
  });

  const patientId = selectedPatient?.id || user?.patient_id || 0;

  // ✅ DEBUG: Log auth state
  console.log('🔍 SixMinuteWalkTest Auth State:', {
    user,
    isAuthenticated,
    selectedPatient,
    patientId,
    authLoading
  });

  // Active tab state
  const [activeTab, setActiveTab] = useState('baseline');

  // Section 1: Baseline Parameters
  const [baselineHR, setBaselineHR] = useState('');
  const [baselineBPSystolic, setBaselineBPSystolic] = useState('');
  const [baselineBPDiastolic, setBaselineBPDiastolic] = useState('');
  const [baselineSpO2, setBaselineSpO2] = useState('');
  const [baselineBorg, setBaselineBorg] = useState<number[]>([0]);

  // Section 2: During Test
  const [duringHR, setDuringHR] = useState('');
  const [duringBPSystolic, setDuringBPSystolic] = useState('');
  const [duringBPDiastolic, setDuringBPDiastolic] = useState('');
  const [duringSpO2, setDuringSpO2] = useState('');
  const [duringBorg, setDuringBorg] = useState<number[]>([0]);

  // Section 2: Post Test
  const [postHR, setPostHR] = useState('');
  const [postBPSystolic, setPostBPSystolic] = useState('');
  const [postBPDiastolic, setPostBPDiastolic] = useState('');
  const [postSpO2, setPostSpO2] = useState('');
  const [postBorg, setPostBorg] = useState<number[]>([0]);

  // Section 3: Results
  const [actualDistance, setActualDistance] = useState('');

  // Section 4: SPPB & Grip Strength
  const [sppbScore, setSppbScore] = useState('');
  const [sppbCategory, setSppbCategory] = useState('');
  const [gripLeft, setGripLeft] = useState('');
  const [gripRight, setGripRight] = useState('');

  // Calculate predicted 6MWT distance (Enright & Sherrill equation)
  const getPredictedDistance = () => {
    const { age, height, weight, gender } = patient;
    if (gender === 'male') {
      return Math.round((7.57 * height) - (5.02 * age) - (1.76 * weight) - 309);
    } else {
      return Math.round((2.11 * height) - (2.29 * weight) - (5.78 * age) + 667);
    }
  };

  // Calculate LLN (approximately predicted - 150m for simplification)
  const getLLN = () => {
    return getPredictedDistance() - 150;
  };

  // Calculate % predicted
  const getPercentPredicted = () => {
    if (!actualDistance) return '';
    const percent = (parseFloat(actualDistance) / getPredictedDistance()) * 100;
    return percent.toFixed(1);
  };

  // Check if all required fields are filled
  const isFormComplete = () => {
    return (
      baselineHR && baselineBPSystolic && baselineBPDiastolic && baselineSpO2 &&
      duringHR && duringBPSystolic && duringBPDiastolic && duringSpO2 &&
      postHR && postBPSystolic && postBPDiastolic && postSpO2 &&
      actualDistance
    );
  };

  // Get Borg scale label
  const getBorgLabel = (value: number) => {
    const labels = [
      'Nothing at all',
      'Very slight',
      'Slight',
      'Moderate',
      'Somewhat severe',
      'Severe',
      '',
      'Very severe',
      '',
      '',
      'Maximal'
    ];
    return labels[value] || '';
  };

  const handleSubmit = () => {
    if (!isFormComplete()) {
      toast.error('Required fields missing', {
        description: 'Please complete all required fields before submitting',
      });
      return;
    }

    // ✅ CRITICAL: Check authentication FIRST before checking patient_id
    if (!isAuthenticated || (!selectedPatient && !user)) {
      console.error('❌ SixMinuteWalkTest - Not authenticated!', {
        isAuthenticated,
        user,
        selectedPatient,
        localStorage_user: localStorage.getItem('user'),
        localStorage_token: localStorage.getItem('access_token')
      });
      toast.error('Not Authenticated', {
        description: 'Your session has expired. Please log in again.',
      });
      return;
    }

    // ✅ Check if patient_id is valid (after auth check)
    if (!patientId || patientId === 0) {
      console.error('❌ SixMinuteWalkTest - No valid patient_id!', {
        patientId,
        selectedPatient,
        user,
        user_patient_id: user?.patient_id
      });
      toast.error('Unable to submit test', {
        description: 'Patient information not available. Please try logging out and logging in again.',
      });
      return;
    }

    // ✅ FIX: Transform data to match backend schema
    // Backend expects: distance_meters, pre_spo2, pre_heart_rate, post_spo2, post_heart_rate, dyspnea_score
    const data = {
      patient_id: patientId,
      distance_meters: parseFloat(actualDistance),
      pre_spo2: parseInt(baselineSpO2),
      pre_heart_rate: parseInt(baselineHR),
      post_spo2: parseInt(postSpO2),
      post_heart_rate: parseInt(postHR),
      dyspnea_score: postBorg[0], // Post-test Borg scale as dyspnea score
      notes: `BP Baseline: ${baselineBPSystolic}/${baselineBPDiastolic}, During: ${duringBPSystolic}/${duringBPDiastolic}, Post: ${postBPSystolic}/${postBPDiastolic}. Borg Baseline: ${baselineBorg[0]}, During: ${duringBorg[0]}, Post: ${postBorg[0]}${sppbScore ? `. SPPB: ${sppbScore} (${sppbCategory})` : ''}${gripLeft || gripRight ? `. Grip Strength L:${gripLeft}, R:${gripRight}` : ''}`,
    };

    console.log('✅ Submitting 6MWT data to backend:', data);

    record6MWT(data)
      .then(() => {
        toast.success('6-Minute Walk Test submitted successfully!', {
          description: 'Results have been saved and shared with your healthcare team',
        });
        // Reset form or navigate back
        if (onBack) onBack();
      })
      .catch((error) => {
        console.error('❌ 6MWT submission error:', error);
        toast.error('Failed to submit test results', {
          description: error.message || 'An error occurred while submitting the test results',
        });
      });
  };

  const predictedDistance = getPredictedDistance();
  const lln = getLLN();
  const percentPredicted = getPercentPredicted();

  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-primary-foreground">6-Minute Walk Test</h1>
            <p className="text-primary-foreground/80">
              {selectedPatient ? `Patient: ${patient.name}` : 'Functional capacity assessment'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 h-auto">
            <TabsTrigger value="baseline" className="flex flex-col gap-1 py-3">
              <ClipboardList className="w-4 h-4" />
              <span className="text-xs">Baseline</span>
            </TabsTrigger>
            <TabsTrigger value="during" className="flex flex-col gap-1 py-3">
              <Activity className="w-4 h-4" />
              <span className="text-xs">During Test</span>
            </TabsTrigger>
            <TabsTrigger value="post" className="flex flex-col gap-1 py-3">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs">Post Test</span>
            </TabsTrigger>
            <TabsTrigger value="results" className="flex flex-col gap-1 py-3">
              <Award className="w-4 h-4" />
              <span className="text-xs">Results</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Baseline Parameters */}
          <TabsContent value="baseline" className="space-y-4">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">Baseline Parameters</CardTitle>
                <p className="text-muted-foreground">Measurements before test (at rest)</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Heart Rate (bpm) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 72"
                      value={baselineHR}
                      onChange={(e) => setBaselineHR(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>SpO₂ (%) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 96"
                      value={baselineSpO2}
                      onChange={(e) => setBaselineSpO2(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Blood Pressure (mmHg) *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Systolic (e.g., 120)"
                      value={baselineBPSystolic}
                      onChange={(e) => setBaselineBPSystolic(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                    <span className="flex items-center text-foreground">/</span>
                    <Input
                      type="number"
                      placeholder="Diastolic (e.g., 80)"
                      value={baselineBPDiastolic}
                      onChange={(e) => setBaselineBPDiastolic(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>
                </div>

                <div className="space-y-3 p-4 bg-[#D4F0F8] rounded-xl">
                  <div className="flex items-center justify-between">
                    <Label>Baseline Borg Scale</Label>
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary">
                      {baselineBorg[0]} - {getBorgLabel(baselineBorg[0])}
                    </Badge>
                  </div>
                  <Slider
                    value={baselineBorg}
                    onValueChange={setBaselineBorg}
                    max={10}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-muted-foreground">
                    <span>0 - No breathlessness</span>
                    <span>10 - Maximal</span>
                  </div>
                </div>

                <Button 
                  className="w-full bg-primary hover:bg-primary/90 h-12"
                  onClick={() => setActiveTab('during')}
                >
                  Continue to During Test
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: During Test Values */}
          <TabsContent value="during" className="space-y-4">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">During Test Values</CardTitle>
                <p className="text-muted-foreground">Measurements at 3 minutes (mid-test)</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Heart Rate (bpm) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 95"
                      value={duringHR}
                      onChange={(e) => setDuringHR(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>SpO₂ (%) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 92"
                      value={duringSpO2}
                      onChange={(e) => setDuringSpO2(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Blood Pressure (mmHg) *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Systolic"
                      value={duringBPSystolic}
                      onChange={(e) => setDuringBPSystolic(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                    <span className="flex items-center text-foreground">/</span>
                    <Input
                      type="number"
                      placeholder="Diastolic"
                      value={duringBPDiastolic}
                      onChange={(e) => setDuringBPDiastolic(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>
                </div>

                <div className="space-y-3 p-4 bg-accent/10 rounded-xl">
                  <div className="flex items-center justify-between">
                    <Label>During Test Borg Scale</Label>
                    <Badge variant="outline" className="bg-accent/20 text-accent border-accent">
                      {duringBorg[0]} - {getBorgLabel(duringBorg[0])}
                    </Badge>
                  </div>
                  <Slider
                    value={duringBorg}
                    onValueChange={setDuringBorg}
                    max={10}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-muted-foreground">
                    <span>0 - No breathlessness</span>
                    <span>10 - Maximal</span>
                  </div>
                </div>

                <Button 
                  className="w-full bg-primary hover:bg-primary/90 h-12"
                  onClick={() => setActiveTab('post')}
                >
                  Continue to Post Test
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Post Test Values */}
          <TabsContent value="post" className="space-y-4">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">Post Test Values</CardTitle>
                <p className="text-muted-foreground">Measurements immediately after test completion</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Heart Rate (bpm) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 105"
                      value={postHR}
                      onChange={(e) => setPostHR(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>SpO₂ (%) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 90"
                      value={postSpO2}
                      onChange={(e) => setPostSpO2(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Blood Pressure (mmHg) *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Systolic"
                      value={postBPSystolic}
                      onChange={(e) => setPostBPSystolic(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                    <span className="flex items-center text-foreground">/</span>
                    <Input
                      type="number"
                      placeholder="Diastolic"
                      value={postBPDiastolic}
                      onChange={(e) => setPostBPDiastolic(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>
                </div>

                <div className="space-y-3 p-4 bg-[#14CAF4]/10 rounded-xl">
                  <div className="flex items-center justify-between">
                    <Label>Post Test Borg Scale</Label>
                    <Badge variant="outline" className="bg-[#14CAF4]/20 text-[#14CAF4] border-[#14CAF4]">
                      {postBorg[0]} - {getBorgLabel(postBorg[0])}
                    </Badge>
                  </div>
                  <Slider
                    value={postBorg}
                    onValueChange={setPostBorg}
                    max={10}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-muted-foreground">
                    <span>0 - No breathlessness</span>
                    <span>10 - Maximal</span>
                  </div>
                </div>

                <Button 
                  className="w-full bg-primary hover:bg-primary/90 h-12"
                  onClick={() => setActiveTab('results')}
                >
                  View Results & Complete Test
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 4: Result Summary */}
          <TabsContent value="results" className="space-y-4">
            <Card className="shadow-lg bg-gradient-to-r from-[#2762AC]/10 to-[#14CAF4]/10 border-primary">
              <CardHeader>
                <CardTitle className="text-primary">Result Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white rounded-xl">
                    <p className="text-muted-foreground">Predicted Distance</p>
                    <h3 className="text-primary">{predictedDistance} m</h3>
                  </div>

                  <div className="p-4 bg-white rounded-xl">
                    <p className="text-muted-foreground">Lower Limit (LLN)</p>
                    <h3 className="text-primary">{lln} m</h3>
                  </div>
                </div>

                <div className="p-6 bg-accent rounded-xl text-center">
                  <Label className="text-accent-foreground">Actual Distance Walked *</Label>
                  <Input
                    type="number"
                    placeholder="Enter distance in meters"
                    value={actualDistance}
                    onChange={(e) => setActualDistance(e.target.value)}
                    className="bg-white border-2 border-border mt-3 text-center h-16"
                  />
                  {actualDistance && (
                    <div className="mt-4 p-4 bg-white rounded-lg">
                      <p className="text-muted-foreground">% Predicted</p>
                      <h2 className="text-accent">{percentPredicted}%</h2>
                      {parseFloat(actualDistance) < lln && (
                        <Badge variant="outline" className="mt-2 bg-destructive/10 text-destructive border-destructive">
                          Below LLN
                        </Badge>
                      )}
                      {parseFloat(actualDistance) >= lln && parseFloat(actualDistance) < predictedDistance && (
                        <Badge variant="outline" className="mt-2 bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
                          Within Normal Range
                        </Badge>
                      )}
                      {parseFloat(actualDistance) >= predictedDistance && (
                        <Badge variant="outline" className="mt-2 bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
                          Above Predicted
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                {actualDistance && parseFloat(actualDistance) < lln && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Below Normal Range</AlertTitle>
                    <AlertDescription>
                      Distance walked is below the Lower Limit of Normal. This may indicate reduced functional capacity. Your healthcare team will review these results.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* SPPB Score & Grip Strength */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">SPPB Score & Grip Strength</CardTitle>
                <p className="text-muted-foreground">Short Physical Performance Battery assessment</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>SPPB Score (0-12)</Label>
                  <Input
                    type="number"
                    placeholder="Enter total score"
                    value={sppbScore}
                    onChange={(e) => setSppbScore(e.target.value)}
                    max={12}
                    min={0}
                    className="bg-white border-2 border-border"
                  />
                  {sppbScore && (
                    <p className="text-muted-foreground">
                      Score: {parseFloat(sppbScore) >= 10 ? 'Minimal limitations' : 
                             parseFloat(sppbScore) >= 7 ? 'Moderate limitations' : 
                             'Severe limitations'}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>SPPB Category</Label>
                  <Select value={sppbCategory} onValueChange={setSppbCategory}>
                    <SelectTrigger className="bg-white border-2 border-border">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minimal">Minimal limitations (10-12)</SelectItem>
                      <SelectItem value="moderate">Moderate limitations (7-9)</SelectItem>
                      <SelectItem value="severe">Severe limitations (0-6)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Left Hand Grip (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="e.g., 25.5"
                      value={gripLeft}
                      onChange={(e) => setGripLeft(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Right Hand Grip (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="e.g., 27.0"
                      value={gripRight}
                      onChange={(e) => setGripRight(e.target.value)}
                      className="bg-white border-2 border-border"
                    />
                  </div>
                </div>

                {gripLeft && gripRight && (
                  <div className="p-4 bg-[#D4F0F8] rounded-xl">
                    <p className="text-muted-foreground">Average Grip Strength</p>
                    <h3 className="text-primary">
                      {((parseFloat(gripLeft) + parseFloat(gripRight)) / 2).toFixed(1)} kg
                    </h3>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Vitals Comparison Summary */}
            {baselineHR && duringHR && postHR && (
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary">Vitals Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                      <span className="text-foreground">Heart Rate</span>
                      <div className="flex gap-4">
                        <span className="text-muted-foreground">
                          <Heart className="w-4 h-4 inline mr-1" />
                          {baselineHR} → {duringHR} → {postHR} bpm
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                      <span className="text-foreground">SpO₂</span>
                      <div className="flex gap-4">
                        <span className="text-muted-foreground">
                          <Activity className="w-4 h-4 inline mr-1" />
                          {baselineSpO2} → {duringSpO2} → {postSpO2}%
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                      <span className="text-foreground">Borg Scale</span>
                      <div className="flex gap-4">
                        <span className="text-muted-foreground">
                          <Gauge className="w-4 h-4 inline mr-1" />
                          {baselineBorg[0]} → {duringBorg[0]} → {postBorg[0]}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Submit Section */}
            <div className="sticky bottom-4 bg-white p-4 rounded-xl shadow-lg border border-border">
              <Button 
                className="w-full bg-accent hover:bg-accent/90 h-12"
                onClick={handleSubmit}
                disabled={!isFormComplete()}
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Submit Test Results
              </Button>
              
              {!isFormComplete() && (
                <p className="text-muted-foreground text-center mt-2">
                  Complete all required (*) fields to submit
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}